import * as React from 'react';
import { BrowseControl } from '../browse-control';
import { ILookupControlProps } from './lookup-control-props';
import './lookup-control.scss';
import { ILookupSelectedObject, IGenericLookupWindowComponentProps } from './generic-lookup-window-component';

const KEY_BACK_SPACE = 8;
const KEY_DELETE = 46;

type LookupControlFunctionComponent = (<TLookupWindowComponentResult extends ILookupSelectedObject[]>(props: ILookupControlProps<TLookupWindowComponentResult>) => JSX.Element) &
  React.FunctionComponent<ILookupControlProps<any>>;

export const lookupControlTestIds = {
  browse: 'lookup-control-browse',
};

export const LookupControl: LookupControlFunctionComponent = <TLookupWindowComponentResult extends ILookupSelectedObject[]>(
  props: ILookupControlProps<TLookupWindowComponentResult>
) => {
  const {
    autoApplySelectedValue,
    value,
    label,
    onRawChange,
    error,
    helperText,
    errorText,
    disabled = false,
    required = false,
    readOnly = false,
    fullWidth = false,
    lookupWindowComponent,
    onValueChanged,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState<TLookupWindowComponentResult>();

  const inputRef = React.useRef<HTMLInputElement>();

  const onCancel = () => {
    setOpen(false);
  };

  const onValueChosen = (newValue: TLookupWindowComponentResult) => {
    setOpen(false);
    if (autoApplySelectedValue) {
      setSelectedValue(newValue);
    }

    onValueChanged(newValue);
  };

  const clearSelection = () => {
    onValueChosen(([] as any) as TLookupWindowComponentResult);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const key: any = event.key || event.keyCode;
    if (key === KEY_DELETE || key === KEY_BACK_SPACE || key === 'Delete' || (key === 'Backspace' && !onRawChange)) {
      clearSelection();
    }
  };

  const computedValue = (): string => {
    let computedVal = value;
    if (autoApplySelectedValue && selectedValue) {
      computedVal = selectedValue;
    }
    return computedVal
      .map(obj => {
        return obj.text;
      })
      .join(', ');
  };

  const handleButtonClick = () => {
    setOpen(true);
  };

  const focusInput = () => {
    inputRef.current!.select();
  };

  const prepareInjectables: () => IGenericLookupWindowComponentProps<TLookupWindowComponentResult> = () => {
    return {
      onValueChosen: (newValue: TLookupWindowComponentResult) => {
        onValueChosen(newValue);
      },
      onCancel: () => {
        onCancel();
      },
    };
  };

  return (
    <>
      <BrowseControl
        label={label}
        onButtonClick={handleButtonClick}
        onClick={focusInput}
        onKeyDown={handleKeyDown}
        onChange={onRawChange}
        value={computedValue()}
        error={error}
        data-testid={lookupControlTestIds.browse}
        helperText={helperText}
        errorText={errorText}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        fullWidth={fullWidth}
        inputRef={inputRef}
      />
      {open && lookupWindowComponent(prepareInjectables())}
    </>
  );
};
